<template>
    <Modal
        name="phoneme"
        class="phoneme-builder"
        :title="$t('editor.ssml.phoneme.title')"
        @closed="onClose"
    >
        <template #head>
            <div class="phoneme-builder__phoneme phoneme-input">
                <div class="phoneme-input__text">
                    <input
                        ref="phoneme"
                        type="text"
                        :value="phoneme"
                        class="phoneme-input__input"
                        @focus="setPhonemeInputPosition"
                        @click="setPhonemeInputPosition"
                        @keydown="onKeyDown"
                        @keyup.backspace="subtract"
                    />
                </div>
                <button
                    :disabled="!phoneme.length"
                    class="phoneme-input__backspace"
                    type="button"
                    @click.prevent="subtract"
                />
                <button :disabled="!valid" class="phoneme-input__save" type="button" @click="save">
                    {{ $t('misc.save') }}
                </button>
            </div>
        </template>
        <template #content>
            <div class="phoneme-builder__inner">
                <FormInput
                    v-if="showSourceWord"
                    v-model="values.key"
                    class="phoneme-builder__source-word"
                    placeholder="Исходное слово"
                    required
                />
                <SpeakerSelect
                    v-if="showSpeakers"
                    v-model="values.speaker"
                    :speakers="speakers"
                    :placeholder="$t('editor.toolbar.pickVoice')"
                    :empty-text="$t('editor.toolbar.noVoices')"
                    emit-prop="slug"
                    required
                    class="phoneme-builder__speakers"
                    width="100%"
                />
                <div class="phoneme-builder__search">
                    <input
                        ref="search"
                        v-model="search"
                        class="phoneme-builder__input"
                        :placeholder="$t(`editor.ssml.phoneme.inputPlaceholder.${searchType}`)"
                        @keyup="onSearchInput"
                    />
                    <RadioGroup
                        v-model="searchType"
                        name="type"
                        :options="$options.searchOptions"
                        class="phoneme-builder__search-type"
                    />
                </div>
                <div class="phoneme-builder__table">
                    <Spinner v-if="loading" />
                    <table v-if="searchType === 'word'" class="phoneme-table">
                        <thead class="phoneme-table__head">
                            <th class="phoneme-table__head-cell" style="width: 40%">
                                {{ $t('misc.word') }}
                            </th>
                            <th class="phoneme-table__head-cell" style="width: 40%">
                                {{ $t('misc.phoneme') }}
                            </th>
                            <th class="phoneme-table__head-cell" style="text-align: right" />
                        </thead>
                        <tbody v-if="words.length">
                            <tr v-for="word in words" :key="word.id" class="phoneme-table__row">
                                <td class="phoneme-table__cell">{{ word.key }}</td>
                                <td class="phoneme-table__cell">
                                    <div class="phoneme-table__withplayer">
                                        <PlayButton @click.prevent="getSample(word.id)" />
                                        {{ word.value }}
                                    </div>
                                </td>
                                <td class="phoneme-table__cell" style="text-align: right">
                                    <button
                                        class="phoneme-table__add"
                                        type="button"
                                        @click="selectWord(word.value)"
                                    />
                                </td>
                            </tr>
                            <tr v-if="canLoadMoreWords" class="phoneme-talbe__row">
                                <td colspan="3" class="phoneme-table__loadmore">
                                    <button class="button" type="button" @click="wordsPage++">
                                        {{ $t('misc.loadmore') }}
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                        <tbody v-else>
                            <tr clas="phoneme-table__row">
                                <td class="phoneme-table__cell" colspan="3">
                                    {{ $t('misc.notfound') }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <template v-else>
                        <div v-if="manualPhonemes.length" class="phoneme-builder__manual">
                            <div style="font-size: 12px; color: #444; margin-bottom: 5px">
                                Special syllables
                            </div>
                            <div class="phoneme-builder__manual-list">
                                <button
                                    v-for="ph in manualPhonemes"
                                    :key="ph.ipa"
                                    type="button"
                                    class="button button--badge phoneme-builder__phoneme-button"
                                    @click.prevent="selectPhoneme(ph.ipa)"
                                >
                                    {{ ph.ipa }}
                                </button>
                            </div>
                        </div>
                        <table class="phoneme-table">
                            <thead class="phoneme-table__head">
                                <th class="phoneme-table__head-cell" style="width: 20%">Viseme</th>
                                <th class="phoneme-table__head-cell" style="width: 20%">IPA</th>
                                <th class="phoneme-table__head-cell" style="width: 50%">
                                    {{ $t('misc.example') }}
                                </th>
                                <th class="phoneme-table__head-cell" style="text-align: right" />
                            </thead>
                            <tbody v-if="filteredPhonemes.length">
                                <tr
                                    v-for="ph in filteredPhonemes"
                                    :key="ph.ipa"
                                    class="phoneme-table__row"
                                >
                                    <td class="phoneme-table__cell">{{ ph.word }}</td>
                                    <td class="phoneme-table__cell">{{ ph.ipa }}</td>
                                    <td class="phoneme-table__cell">
                                        {{ ph.example }}
                                    </td>
                                    <td class="phoneme-table__cell" style="text-align: right">
                                        <button
                                            class="phoneme-table__add"
                                            type="button"
                                            @click="selectPhoneme(ph.ipa)"
                                        />
                                    </td>
                                </tr>
                            </tbody>
                            <tbody v-else>
                                <tr clas="phoneme-table__row">
                                    <td class="phoneme-table__cell" colspan="4">
                                        {{ $t('misc.notfound') }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </template>
                </div>
            </div>
        </template>
    </Modal>
</template>

<script>
import { FormInput } from '@speechki-frontend/shared/ui/forms';
import { Modal } from '@speechki-frontend/shared/ui/modals';
import { PlayButton } from '@speechki-frontend/shared/ui/Player';
import { RadioGroup } from '@speechki-frontend/shared/ui/forms';
import SpeakerSelect from '@speechki-frontend/shared/ui/SpeakerSelect';
import { mapState, mapActions } from 'vuex';

import { fetchPhonemes } from '../../api/phonemes';

const KEYCODES = [37, 39];
const SEARCH_OPTIONS = [
    { id: 'syllable', name: 'Syllable' },
    { id: 'word', name: 'Word' },
];

export default {
    name: 'PhonemeModal',
    components: {
        FormInput,
        Modal,
        PlayButton,
        RadioGroup,
        SpeakerSelect,
    },
    props: {
        speaker: {
            type: String,
            default: '',
        },
        value: {
            type: String,
            default: '',
        },
        phonemes: {
            type: Array,
            default: () => [],
        },
        showSourceWord: {
            type: Boolean,
            default: false,
        },
        showSpeakers: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            phoneme: '',
            words: [],
            wordsCount: 0,
            wordsPageSize: 10,
            wordsPage: 1,
            search: '',
            searchTimeout: null,
            phonemeSelection: null,
            searchType: 'syllable',
            loading: false,
            values: {
                key: '',
                speaker: '',
            },
        };
    },
    searchOptions: SEARCH_OPTIONS,
    computed: {
        ...mapState({
            speakers: (state) => state.settings.speakers,
        }),
        valid() {
            let result = !!this.phoneme.length && this.phoneme !== this.value;
            if (this.showSourceWord) result = result && this.values.key.length;
            if (this.showSpeakers) result = result && this.values.speaker;
            return result;
        },
        canLoadMoreWords() {
            return this.words.length && this.wordsPageSize * this.wordsPage < this.wordsCount;
        },
        filteredPhonemes() {
            return this.phonemes.filter((phoneme) => phoneme.word === this.search);
        },
        speakerId() {
            const { speaker } = this.values;
            if (!speaker) return 0;
            return this.speakers.find(({ slug }) => slug === speaker).id;
        },

        manualPhonemes() {
            return this.phonemes.filter((ph) => ph.is_manual);
        },
    },
    watch: {
        search() {
            if (this.searchType === 'word') {
                this.wordsCount = 0;
                this.wordsPage = 1;
            }
        },
        wordsPage(value) {
            this.fetchWords(value);
        },
        searchType() {
            if (!this.search.length) return;

            if (this.searchType === 'word') {
                this.wordsCount = 0;
                this.wordsPage = 1;
                this.fetchWords();
            }
        },
        'values.speaker'(slug) {
            this.$emit('selectSpeaker', slug);
        },
    },
    async created() {
        if (this.value) {
            this.phoneme = this.value;
        }
        if (this.showSpeakers) {
            await this.fetchSpeakers();
        }
    },
    mounted() {
        this.$refs.search.focus();
    },
    methods: {
        ...mapActions(['fetchSpeakers']),
        onKeyDown(event) {
            if (!KEYCODES.includes(event.keyCode)) {
                event.preventDefault();
            }
        },
        subtract() {
            if (!this.phoneme.length) return;
            const { selectionStart } = this.$refs.phoneme;
            if (selectionStart === 0) {
                return;
            }
            this.phoneme =
                this.phoneme.slice(0, selectionStart - 1) + this.phoneme.slice(selectionStart);
            this.$refs.phoneme.focus();
            this.$nextTick(() => {
                this.$refs.phoneme.setSelectionRange(selectionStart - 1, selectionStart - 1);
                this.selectionStart = selectionStart - 1;
            });
        },
        async fetchWords() {
            this.loading = true;
            const params = {
                search: this.search,
                page_size: this.wordsPageSize,
                page: this.wordsPage,
            };

            const { results, count } = await fetchPhonemes(params);

            if (this.wordsPage > 1) {
                this.words = [...this.words, ...results];
            } else {
                this.words = results;
            }
            this.wordsCount = count;
            this.loading = false;
        },
        onSearchInput() {
            if (this.searchType === 'syllable') return;

            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout);
                this.searchTimeout = null;
            }

            this.searchTimeout = setTimeout(() => {
                this.fetchWords();
            }, 500);
        },
        selectWord(phoneme) {
            this.phoneme = phoneme;
        },
        selectPhoneme(ipa) {
            const selectionStart = this.phonemeSelection || this.$refs.phoneme.selectionStart;
            this.phoneme =
                this.phoneme.slice(0, selectionStart) + ipa + this.phoneme.slice(selectionStart);
            this.phonemeSelection = this.phonemeSelection + ipa.length;
            this.search = '';
            this.$refs.search.focus();
        },
        save() {
            const payload = {
                key: null,
                phoneme: this.phoneme,
                save: true,
            };

            if (this.showSourceWord) payload.key = this.values.key;
            if (this.showSpeakers) payload.speaker = this.speakerId;

            this.$emit('saved', payload);
            this.$emit('close');
        },
        setPhonemeInputPosition() {
            this.phonemeSelection = this.$refs.phoneme.selectionStart;
        },
        getSample(id) {
            this.$emit('playSample', id);
        },
        onClose() {
            this.$emit('close');
        },
    },
};
</script>
